package org.multiplatform.kotfolio.pages.koin

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ul
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties

@Page("annotations")
@Composable
fun KoinAnnotationsPage() {
    BlogLayout(
        PageProperties(
            "Koin Annotations",
            ""
        ),
        BlogProperties("Koin", "Annotations")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            KotlinCode(
                code = """
                    ksp = "2.3.4"
                    
                    koin = "4.1.1"
                    koinAnnotations = "2.3.1"
                    koinNav3 = "4.2.0-beta4"
                    
                    [libraries]
                    # Koin 4.x & Annotations
                    koin-android = { module = "io.insert-koin:koin-android", version.ref = "koin" }
                    koin-compose = { module = "io.insert-koin:koin-androidx-compose", version.ref = "koin" }
                    koin-annotations = { module = "io.insert-koin:koin-annotations", version.ref = "koinAnnotations" }
                    koin-ksp-compiler = { module = "io.insert-koin:koin-ksp-compiler", version.ref = "koinAnnotations" }
                    koin-compose-navigation3 = { module = "io.insert-koin:koin-compose-navigation3", version.ref = "koinNav3" }
                    
                    [bundles]
                    koin = ["koin-android", "koin-compose", "koin-annotations"]
                    navigation3 = ["androidx-navigation3-runtime", "androidx-navigation3-ui", "koin-compose-navigation3"]
                    
                    ksp = { id = "com.google.devtools.ksp", version.ref = "ksp" }
                    
                    plugins {
                        alias(libs.plugins.ksp)
                    }

                    ksp {
                        arg("KOIN_CONFIG_CHECK", "true")
                        arg("KOIN_LOG_TIMES", "true")
                    }
                    
                    dependencies {
                        implementation(libs.transportation.consumer)
                        dependencies {
                            // Android Core & Lifecycle
                            implementation(libs.androidx.lifecycle.viewmodel.navigation3)

                            // Bundles (Koin, Ktor, Navigation)
                            implementation(libs.bundles.koin)
                            implementation(libs.bundles.navigation3)

                            // KSP Compiler
                            ksp(libs.koin.ksp.compiler)

                        }
                    }
                    
                    //gradle.properties
                    android.useAndroidX=true
                    # Kotlin code style for this project: "official" or "obsolete":
                    kotlin.code.style=official
                    # Enables namespacing of each library's R class so that its R class includes only the
                    # resources declared in the library itself and none from the library's dependencies,
                    # thereby reducing the size of the R class for that library
                    android.nonTransitiveRClass=true
                    android.defaults.buildfeatures.resvalues=true
                    android.sdk.defaultTargetSdkToCompileSdkIfUnset=false
                    android.enableAppCompileTimeRClass=false
                    android.usesSdkInManifest.disallowed=false
                    android.uniquePackageNames=false
                    android.dependency.useConstraints=true
                    android.r8.strictFullModeForKeepRules=false
                    android.r8.optimizedResourceShrinking=false
                    android.builtInKotlin=false
                    android.newDsl=false
                    """.trimIndent()
            )

            SpanText(
                text = "Koin Annotations",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Koin Annotations: It uses annotation processing (KSP) to generate DI modules automatically.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Koin DSL (Manual Modules): DSL stands for Domain-Specific Language — it’s the classic Koin style using Kotlin functions and lambdas.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Koin Annotations 2.2",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "@ActivityScope — Scoped to the Activity lifecycle.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "@ActivityRetainedScope — Survives configuration changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "@FragmentScope — Scoped to Fragment lifecycle",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "@ViewModelScope — Compatible with Android ViewModel & Compose Multiplatform",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "@Configuration - annotation allows a class module to be tagged into a set of configurations.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "@Monitor - Koin automatically generates monitoring proxies around your components, and manages the Koin declaration for you.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            //https://blog.insert-koin.io/koin-annotations-2-0-release-is-out-445c27a569b9
            SpanText(
                text = "Koin Annotations 2.0",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            SpanText(
                text = "ViewModel Definitions",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Tag your classes with annotations like @Factory, @Single, @KoinViewModel, or @KoinWorker to follow Koin’s definition semantics. Automatic bindings are detected, so your extended types are added automatically (with the option to specify them if necessary).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Automatic Binding",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Definitions are created with automatic binding using the specified interface type.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Qualifiers",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "@Named for string qualifiers or @Qualifier for type-based qualifiers. You can even create custom qualifiers by extending the @Qualifier annotation.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Kotlin Multiplatform Support",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "All annotated definitions can be used in your Kotlin Multiplatform commonMain sourceSet, including @KoinViewModel (with Compose ViewModel support).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Module Creation",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Annotate your classes with @Module to create a module. Definitions can be scanned using @ComponentScan or declared as simple functions. Module scanning can span across all Gradle modules in your project.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Native Components Sharing",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Share native components via an expect/actual contract.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Compile Safety and Time Logging",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Compile safety is optional — you can enable it if needed. Additionally, time logging can be activated to track build times.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                        ksp {
                            arg("KOIN_CONFIG_CHECK", "true")
                            arg("KOIN_LOG_TIMERS", "true")
                        }
                    """.trimIndent()
            )

            //https://github.com/VolodymyrPal/TrainingSession.git
            KotlinCode(
                code = """
                        //koin annotations
                        
                        //koingraph.kt
                        @KoinApplication(modules = [CommonModule::class])
                        object MyApp

                        @Module(includes = [NativeModuleD::class])
                        @ComponentScan("org.trainingsession.project")
                        class CommonModule {

                            @Factory
                            fun provideHttpClient(): HttpClient = HttpClient() {
                                install(DefaultRequest) {

                                }
                                install(Logging) {
                                    logger = MyKtorLogger
                                }
                                install(ContentNegotiation) {
                                    json(
                                        Json {
                                            ignoreUnknownKeys = true
                                            encodeDefaults = true
                                            prettyPrint = true
                                        }
                                    )
                                }
                                install(HttpCache)
                            }

                        }

                        @Module
                        expect class NativeModuleD() {
                            @Factory
                            fun providesPlatformComponentSample(scope: Scope): PlatformComponentSample
                        }

                        interface PlatformComponentSample {

                        }

                        fun initKoin(config: KoinAppDeclaration? = null) {
                            MyApp.startKoin {
                                printLogger(Level.DEBUG)
                                config?.invoke(this)
                            }
                        }

                        fun initKoinWithIos(config: KoinAppDeclaration? = null, iosDependencies: IOSDependencies) {
                            initKoin {
                                config?.invoke(this)
                                modules(
                                    platformModule(iosDependencies)
                                )
                            }
                        }

                        interface IOSDependencies {}

                        fun platformModule(deps: IOSDependencies) = module {
                            single<IOSDependencies> { deps }
                        }
                        
                        //AppLogger.kt
                        expect object AppLogger {
                            fun e(tag: String, message: String, throwable: Throwable?)
                            fun d(tag: String, message: String)
                            fun i(tag: String, message: String)
                        }

                        private const val KTOR_TAG = "KtorClient"

                        object MyKtorLogger : Logger {
                            override fun log(message: String) {
                                AppLogger.d(KTOR_TAG, message)
                            }
                        }
                        
                        @Single(binds = [ProgramRepository::class])
                        class ProgramRepositoryImpl() : ProgramRepository { }
                        
                        @KoinViewModel
                        class WorkoutScreenViewModel(
                            private val repository: ProgramRepository,
                            @InjectedParam
                            val programId: Int
                        ) : ViewModel() { }
                        
                        @Composable
                        fun App(
                            viewModel: SettingsViewModel = koinViewModel(), 
                            viewModel: WorkoutScreenViewModel = koinViewModel(parameters = { parametersOf(1) } )
                        ) {}
                        
                        @Composable
                        @Preview
                        fun App() {
                            val platform = koinInject<Platform>()
                            AppLogger.d(platform.name, "Was initialized")
                        }
                        
                        //KoinGraph.ios.kt
                        import org.koin.core.annotation.Factory
                        import org.koin.core.annotation.Module
                        import org.koin.core.scope.Scope

                        @Module
                        actual class NativeModuleD actual constructor() {
                            @Factory
                            actual fun providesPlatformComponentSample(scope: Scope): PlatformComponentSample {
                                return object : PlatformComponentSample {}
                            }
                        }
                        
                        //KoinGraph.android.kt
                        import org.koin.core.annotation.Factory
                        import org.koin.core.annotation.Module
                        import org.koin.core.scope.Scope

                        @Module
                        actual class NativeModuleD {
                            @Factory
                            actual fun providesPlatformComponentSample(scope: Scope): PlatformComponentSample =
                                PlatformComponentDiOS()
                        }

                        class PlatformComponentDiOS : PlatformComponentSample {
                        }
                        
                        @main
                        struct iOSApp: App {
                            init() {
                                let deps = IOSDependenciesImpl()
                                KoinGraphKt.doInitKoinWithIos(
                                    config: nil,
                                    iosDependencies: deps
                                )
                            }
                        }
                        
                        //android
                        class AppApplication : Application() {
                            override fun onCreate() {
                                super.onCreate()
                                initKoin {
                                    androidContext(this@AppApplication)
                                }
                            }
                        }
                    """.trimIndent()
            )

            //https://github.com/InsertKoinIO/koin-annotations
            KotlinCode(
                code = $$"""
                        //1
                        @KoinApplication
                        object KoinApp

                        fun initKoin(config : KoinAppDeclaration ?= null) {
                            KoinApp.startKoin {
                                includes(config)

                                // Activate App analyses & Perf tracing
                                // Check kotzilla.json file
                                // analytics()
                            }

                            val msgA = KoinPlatform.getKoin().get<PlatformComponentA>().sayHello()
                            println("KMP App Platform: $msgA")
                        }
                        
                        //2
                        interface MuseumStorage { }

                        @Single
                        class InMemoryMuseumStorage : MuseumStorage {
                        }
                    """.trimIndent()
            )

            SpanText(
                text = "koin annotations",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            SpanText(
                text = "@ScopeId",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "You may need to resolve a component from another scope, that is not directly accessible to your scope. For this you need to tag your dependency with @ScopeId annotation, to tell Koin to find this dependency in a scope of given scope Id.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "@Property",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Koin Annotations allow to inject a Koin property with @Property annotation. Now, you can define a Kotlin field as a default value for this property, using @PropertyValue",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    @Factory
                    public class ComponentWithProps(
                        @Property("id") public val id : String
                    )
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Factory
                    public class ComponentWithProps(@Property("id") public val id: String)

                    @PropertyValue("id")
                    val DEFAULT_ID: String = "_empty_id"
                    """.trimIndent()
            )

            SpanText(
                text = "@ComponentScan",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "With Koin Annotations 1.4.0, you can now scan for definitions across multiple Gradle modules. By annotating a module class with @Module and adding @ComponentScan(“your.package.here”), you can scan annotated classes within a specified package.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    @Module
                    @ComponentScan("com.jetbrains.kmpapp.data")
                    class DataModule {
                        @Single
                        fun json() = Json { ignoreUnknownKeys = true }

                        @Single
                        fun httpClient(json : Json) = HttpClient {
                            install(ContentNegotiation) {
                                json(json, contentType = ContentType.Any)
                            }
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/Beb3r/masterdetailshowcase.git
            KotlinCode(
                code = """
                    koin-annotations = "2.1.0"
                    koin = "4.1.0"
                    
                    koin = { group = "io.insert-koin", name = "koin-android", version.ref = "koin" }
                    koin-annotations = { group = "io.insert-koin", name = "koin-annotations", version.ref = "koin-annotations" }
                    koin-compose = { group = "io.insert-koin", name = "koin-androidx-compose", version.ref = "koin" }
                    koin-compose-navigation = { group = "io.insert-koin", name = "koin-androidx-compose-navigation", version.ref = "koin" }
                    koin-ksp-compiler = { group = "io.insert-koin", name = "koin-ksp-compiler", version.ref = "koin-annotations" }
                    
                    startKoin {
                        androidLogger()
                        androidContext(this@AppApplication)

                        // TODO plant debug tree only in debug builds
                        Timber.plant(Timber.DebugTree())

                        modules(
                            CommonModule().module,
                            NetworkModule().module,
                        )
                    }
                    
                    @Module
                    @ComponentScan("com.gromo.masterdetailshowcase.libraries.network")
                    class NetworkModule

                    @Single
                    internal fun provideHttpClient() = HttpClient(Android) {
                        install(ContentNegotiation) {
                            json(Json {
                                prettyPrint = true
                                ignoreUnknownKeys = true
                            })
                        }

                        defaultRequest {
                            headers {
                                append(HttpHeaders.ContentType, ContentType.Application.Json)
                            }
                            url("https://rickandmortyapi.com/api/")
                        }

                        install(Logging) {
                            level = LogLevel.INFO
                        }
                    }
                    
                    @Module
                    @ComponentScan("com.gromo.masterdetailshowcase.libraries.common")
                    class CommonModule

                    @Single
                    fun createDispatchers(): AppCoroutineDispatchers =
                        AppCoroutineDispatchers(
                            io = Dispatchers.IO,
                            computation = Dispatchers.Default,
                            main = Dispatchers.Main,
                        )

                    @Factory
                    fun createAppScope(): CoroutineScope {
                        val supervisorJob = SupervisorJob()
                        return CoroutineScope(Dispatchers.IO + supervisorJob)
                    }
                    
                    data class AppCoroutineDispatchers(
                        val io: CoroutineDispatcher,
                        val computation: CoroutineDispatcher,
                        val main: CoroutineDispatcher,
                    )
                    
                    @KoinViewModel
                    class HomeViewModel(
                        private val coroutineDispatcher: AppCoroutineDispatchers
                    ) : ViewModel() { }
                    
                    @Factory
                    class FetchAllCharactersUseCase(private val repository: CharactersRepository) {
                        suspend operator fun invoke() = repository.fetchAllCharacters()
                    }
                    
                    @Single
                    class CharactersRepository(
                        private val remoteDataSource: CharactersRemoteDataSource,
                        private val localDataSource: CharactersLocalDataSource,
                    ) { }
                    
                    @Single(binds = [SessionLocalDataSource::class])
                    class SessionLocalDataSourceImpl(
                        context: Context
                    ) : SessionLocalDataSource { }
                    
                    interface SessionLocalDataSource { }
                    """.trimIndent()
            )

            SpanText(
                text = "@Module",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This marks the class as a Koin module. Instead of writing a manual module f ...} block, the KSP compiler will generate the necessary DSL for us. This annotation is used to define a Koin module. A module is a collection of dependencies that can be loaded into the Koin container at runtime. You can define a module by creating a class and annotating it with @Module, then defining your dependencies using Koin DSL functions like single(), factory(), and scoped().",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    @Module(includes = [DataModule::class])
                    @ComponentScan("")
                    class AppModule
                    
                    private const val DATA_COMPONENT_SCAN = ""
                    @Module(includes = [NetworkModule::class])
                    @ComponentScan(DATA_COMPONENT_SCAN)
                    class DataModule {
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "@Single",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This tells Koin to treat the returned object as a Singleton. This ensures we reuse the same HttpClient instance across the entire application, which is much more resource-efficient than creating a new one for every request.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    //Nullable Dependencies
                    
                    @Single
                    class MyComponent(val myDependency : MyDependency?)
                    
                    single { MyComponent(getOrNull()) }
                    """.trimIndent()
            )

            //https://github.com/Ma7moud3ly/quran-multiplatform.git
            KotlinCode(
                code = """
                     //Font
                    
                     /**
                      * Manages available fonts within the application.
                      *
                      * Provides a centralized list of `AppFont` objects, each with an ID, name,
                      * and font resource. Includes a default font and methods to retrieve all fonts
                      * or a specific font by ID.
                      */
                     @Single
                     class FontsManager() {
                         companion object {
                             private val fonts = listOf(
                                 AppFont(
                                     id = "",
                                     name = "",
                                     fontType = Res.font.
                                 ), 
                                 AppFont(
                                     id = "",
                                     name = "",
                                     fontType = Res.font.
                                 ),
                                 AppFont(
                                     id = "",
                                     name = " ",
                                     fontType = Res.font.
                                 ),
                                 AppFont(
                                     id = "",
                                     name = "",
                                     fontType = Res.font.,
                                     letterSpacing = 2.sp,
                                     lineHeight = 2.5.em
                                 )
                             )

                             const val DEFAULT_FONT = ""
                             fun first() = fonts.firstOrNull { it.id == DEFAULT_FONT } ?: fonts.first()
                         }

                         fun getFonts(): List<AppFont> = fonts
                         fun getFont(id: String) = fonts.firstOrNull { it.id == id } ?: fonts.first()
                     }
                     
                     //access
                     FontsManager().getFonts()
                """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Module
                    class NetworkModule {

                        @Single
                        fun provideHttpEngine(): HttpClientEngine = CIO.create()

                        @Single
                        fun provideHttpClient(httpClientEngine: HttpClientEngine): HttpClient {
                            return HttpClient(httpClientEngine) {
                                install(ContentNegotiation) {
                                    json(Json {
                                        prettyPrint = true
                                        isLenient = true
                                        ignoreUnknownKeys = true
                                    })
                                }
                                install(Logging) {
                                    logger = Logger.SIMPLE
                                    level = LogLevel.ALL
                                }
                            }
                        }

                        @Single
                        fun provideBaseUrl(): String = ""
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "@InjectedParam",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    @Single
                    class MyComponent(@InjectedParam val myDependency : MyDependency)
                    
                    val m = MyDependency()
                    // Resolve MyComponent while passing MyDependency
                    koin.get<MyComponent> { parametersOf(m) }
                    """.trimIndent()
            )

            SpanText(
                text = "@Singleton",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This annotation is used to define a singleton dependency. A singleton is an object that is created once and reused throughout the lifetime of the application. You can annotate a class or a function with @Singleton to indicate that it should be treated as a singleton by Koin. single function is used to create a single instance of a dependency that is shared across the entire app.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "@Factory",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This annotation is used to define a factory dependency. A factory is an object that creates and returns a new instance of a class each time it is called. You can annotate a class or a function with @Factory to indicate that it should be treated as a factory by Koin.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            //https://github.com/IntSoftDev/LondonTubeStatus.git
            KotlinCode(
                code = $$"""
                    import io.ktor.client.plugins.logging.LogLevel
                    import io.ktor.client.plugins.logging.Logger
                    import io.ktor.client.plugins.logging.Logging
                    import io.ktor.http.isSuccess
                    import io.ktor.serialization.kotlinx.json.json
                    import kotlinx.serialization.json.Json
                    import org.koin.core.qualifier.named
                    import org.koin.dsl.module

                    const val TIMEOUT_DURATION: Long = 10_000

                    val networkModule =
                        module {
                            single {
                                Json {
                                    ignoreUnknownKeys = true
                                    isLenient = true
                                    explicitNulls = false
                                    prettyPrint = true
                                }
                            }

                            single(named(HTTP_CLIENT)) {
                                HttpClient {
                                    expectSuccess = true
                                    install(ContentNegotiation) {
                                        json(get())
                                    }
                                    install(Logging) {
                                        logger =
                                            object : Logger {
                                                override fun log(message: String) {
                                                    Napier.d(tag = "HTTP") { message }
                                                }
                                            }
                                        level = LogLevel.INFO
                                    }

                                    install(HttpRequestRetry) {
                                        retryIf { request, response ->
                                            !response.status.isSuccess()
                                        }
                                        exponentialDelay()
                                    }

                                    install(HttpTimeout) {
                                        connectTimeoutMillis = TIMEOUT_DURATION
                                        requestTimeoutMillis = TIMEOUT_DURATION
                                        requestTimeoutMillis = TIMEOUT_DURATION
                                        socketTimeoutMillis = TIMEOUT_DURATION
                                    }
                                }
                            }

                            factory<TFLStatusAPI> {
                                TFLStatusProxy(
                                    httpClient = get(named(HTTP_CLIENT)),
                                    appId = getPropertyOrNull(APP_ID_KEY),
                                    appKey = getPropertyOrNull(APP_KEY),
                                )
                            }
                        }

                    internal const val HTTP_CLIENT = "TflStatusHttpClient"
                    
                    internal interface TFLStatusAPI {
                        suspend fun getLineStatuses(lineIds: String): List<TFLStatusResponseItem>
                    }

                    // https://api.tfl.gov.uk/Line/victoria,central/Status?detail=false&appId=appId&appKey=appKey
                    internal class TFLStatusProxy(
                        private val httpClient: HttpClient,
                        private val appId: String?,
                        private val appKey: String?,
                    ) : TFLStatusAPI {
                        private val tflAPIEndPoint = "https://api.tfl.gov.uk/"

                        override suspend fun getLineStatuses(lineIds: String): List<TFLStatusResponseItem> {
                            Napier.d("calling httpClient")
                            if (appId == null || appKey == null) {
                                // response might get throttled
                                return httpClient.get("$tflAPIEndPoint/Line/$lineIds/Status?detail=false")
                                    .body()
                            }
                            return httpClient.get("$tflAPIEndPoint/Line/$lineIds/Status?detail=false&appId=$appId&appKey=$appKey")
                                .body()
                        }
                    }
                    
                    interface TFLServicesRepository {
                        suspend fun getLineStatuses(lineIds: String): Result<List<TFLStatusResponseItem>>
                    }
                    
                    internal class TFLServicesRepositoryImpl(
                        private val tflStatusAPI: TFLStatusAPI,
                    ) : TFLServicesRepository {
                        override suspend fun getLineStatuses(lineIds: String): Result<List<TFLStatusResponseItem>> {
                            try {
                                val result = tflStatusAPI.getLineStatuses(lineIds)
                                return Result.success(result)
                            } catch (e: IOException) {
                                return Result.failure(ApiException.IOException(e.message))
                            } catch (e: Exception) {
                                return Result.failure(ApiException.HttpError(message = e.message))
                            }
                        }
                    }
                    
                    class GetTFLStatusUseCase(
                        private val tflServicesRepository: TFLServicesRepository,
                    ) {
                        suspend operator fun invoke(lines: String): Result<List<TFLStatusResponseItem>> {
                            return tflServicesRepository.getLineStatuses(lines)
                        }
                    }
                    
                    val domainModule =
                        module {
                            includes(dataModule)
                            factoryOf(::GetTFLStatusUseCase)
                        }
                    
                    val tflStatusDiModule =
                        module {
                            includes(domainModule)
                            viewModelOf(::TubeStatusViewModel)
                        }
    
                    val dataModule =
                        module {
                            includes(networkModule)
                            single<TFLServicesRepository> {
                                TFLServicesRepositoryImpl(
                                    tflStatusAPI = get(),
                                )
                            }
                        }  
                    
                    val tflStatusDiModule =
                        module {
                            includes(domainModule)
                            viewModelOf(::TubeStatusViewModel)
                        }
                    
                    //androidMain
                    fun initTflSDK(
                        context: android.content.Context,
                        koinApplication: KoinApplication? = null,
                        enableLogging: Boolean = true,
                    ) {
                        if (enableLogging) {
                            Napier.base(DebugAntilog())
                        }
                        if (koinApplication == null) {
                            startKoin {
                                androidContext(context)
                                modules(tflStatusDiModule)
                            }
                        } else {
                            koinApplication.androidContext(context)
                            koinApplication.koin.loadModules(listOf(tflStatusDiModule))
                        }
                    }
                    
                    //iosMain
                    fun initTflSDK(
                        enableLogging: Boolean = true,
                    ) {
                        if (!sdkInitialised) {
                            if (enableLogging) {
                                Napier.base(DebugAntilog())
                            }
                            startKoin {
                                modules(tflStatusDiModule)
                            }
                            sdkInitialised = true
                        } else {
                            Napier.w("Tfl SDK already initialised")
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "@Scoped",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This annotation is used to define a scoped dependency. A scoped dependency is a dependency that is tied to a specific scope, such as a particular activity or fragment. You can annotate a class or a function with @Scoped to indicate that it should be scoped to a specific context.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "@Named",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This annotation is used to provide a name for a dependency. You can use @Named to differentiate between multiple dependencies of the same type, or to provide additional context for a dependency. Used when one needs multiple instances of the same class, differentiated by a name.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "@Inject",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This annotation is used to inject a dependency into a class or a function. You can annotate a constructor parameter, a property, or a function parameter with @Inject to indicate that Koin should provide the dependency automatically.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    //Dynamic Injection

                    val repository: Repository = get()
                    """.trimIndent()
            )

            SpanText(
                text = "@get",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This annotation is used to retrieve a dependency from Koin. You can use @get to retrieve a dependency by its type or name, either directly or through a property or function.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "workManagerFactory",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Start WorkManager components declared in Koin",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "androidLogger",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Enable Koin logging on an Android",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "androidContext",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Reference the Android Application context",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "isolated Context with Compose",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "You can work the same way with an isolated context to deal with SDK or white label application, in order to not mix your Koin definitions with an end user's one.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Koin’s Isolated Context allows you to define a completely independent DI graph that operates separately from your main application’s modules configuration. This feature is invaluable when you need temporary or isolated setups without interfering with the primary DI environment.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Use Context Isolation to create a completely independent instance of Koin, managing all objects for your library without affecting the main application’s setup.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Declare our isolated context holder, in order to store our isolated Koin instance in memory. This can be done with a simple Object class like this. The MyIsolatedKoinContext class is holding our Koin instance.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Setting Up Koin’s Isolated Context",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The first step is to create an object class that will maintain your isolated context instance. This class should be a singleton, ensuring it remains accessible throughout the entire lifecycle of your library.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    object MyIsolatedKoinContext {

                        val koinApp = koinApplication {
                            modules(myModule)
                        }

                        val koin = koinApp.koin
                    }
                    """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "koinApp is the Koin application instance that initializes with the provided modules.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "koin is the Koin instance, which will be used to resolve dependencies within your library.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    //https://github.com/santimattius/kmp-entertainment-app (Main Branch)

                    object FavoriteContext {
                        val koinApp = koinApplication {
                        modules (favoriteModule, platformModule, sharedModules)
                    }

                    val favoriteModule = module {
                        viewModelOf(:: FavoriteViewModel)
                    }

                    @Composable
                    fun FavoriteRoute (onFavoriteClick: (FavoriteUiModel) → Unit {
                        KoinIsolatedContext (FavoriteContext.koinApp) {
                            val viewModel = koinViewModel<FavoriteViewModel>()
                            FavoriteScreen(viewModel, onFavoriteClick)
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/orioneee/Axer
            KotlinCode(
                code = """
                    MaterialTheme(BlueTheme) {
                        KoinIsolatedContext(IsolatedContext.koinApp) {

                        }
                    }

                    import org.koin.core.KoinApplication
                    internal object IsolatedContext {
                        lateinit var koinApp: KoinApplication
                            private set

                        val koin by lazy {
                            koinApp.koin
                        }

                        fun initIfNotInited(
                            application: KoinApplication
                        ) {
                            if (!::koinApp.isInitialized) {
                                koinApp = application
                            }
                        }
                    }

                    //Access
                    //val context: Context = IsolatedContext.koinApp.koin.get()
                    """.trimIndent()
            )

            SpanText(
                text = "@koinInject",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Utilize the `koinInject()` function within your composables to fetch dependencies, ensuring that instances are cached and tied to the Composable's lifecycle, reducing overhead. Instances injected via koinInject will follow the lifecycle of the Composable they are used in, releasing the instance when the Composable is disposed. ViewModel instances will adhere to the standard ViewModel lifecycle.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Use Case: For repositories, use cases, helpers, or services — basically non-ViewModel objects.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "@KoinViewModel",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Using @KoinViewModel annotation generates code with an Android definition. For projects using Compose Multiplatform, you can configure your module to use the Compose ViewModel DSL instead of Android Compose DSL, by adding a KSP section with USE_COMPOSE_VIEWMODEL key to your build.gradle.kts file:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Use Case: For ViewModel classes (including StateFlow, LiveData, etc.) that need to survive configuration changes and be tied to a navigation back stack.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    //No Annotations 
                    
                    @Composable
                    fun FAQScreen(
                        viewModel: FAQViewModel = koinViewModel()
                    ) { }
                    
                    class FAQViewModel() : ViewModel() { }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    ksp {
                        arg("USE_COMPOSE_VIEWMODEL","true")
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @KoinViewModel
                    class PostViewModel(private val repository: PostRepository) : ViewModel() {
                    }
                    
                    @OptIn(ExperimentalMaterial3Api::class)
                    @Composable
                    fun CreatePostScreen(
                        viewModel: PostViewModel,
                        onBack: () -> Unit
                    ) {
                    }
                    
                    CreatePostScreen(
                        viewModel = koinViewModel(),
                        onBack = {  }
                    )
                    """.trimIndent()
            )
        }
    }
}

//https://github.com/Ma7moud3ly/quran-multiplatform.git
//https://github.com/VolodymyrPal/TrainingSession.git
//https://github.com/InsertKoinIO/koin-annotations
//https://github.com/VolodymyrPal/TrainingSession.git (List Mapper, Koin Annotations)
//https://github.com/vincenzosarnataro/UsersKMP.git (Custom Header, Retrofit, Koin Annotations)
//https://github.com/AnatoliiChub/HRAM (Koin Annotation  @WorkerThread, @WorkerIOThread, @Qualifier, @Module, @Module(includes = []), @Configuration, @Single, @Single(binds = []),  @Factory, @KoinViewModel, )
//https://medium.com/@spparks_/koin-scaling-your-project-with-annotations-ksp-and-navigation-3-integration-273abe767a4b
//https://github.com/Mikkareem/DosthMan.git
//https://github.com/shub39/PennyPal.git
//https://medium.com/@felix.lf/a-guide-to-modern-dependency-injection-in-kmp-with-koin-annotations-dcc086a976f3
//https://github.com/Grigoriym/Wallosmobile.git